"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { useAuthContext } from "@/contexts/common/AuthProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { captureAuthzDenied } from "@/utils/authz/captureAuthzDenied";
import { decideRouteAccess, UNAUTHORIZED_PATH } from "@/utils/authz/routeAccess";
import { useRouter } from "@bprogress/next";

/**
 * Enforces the route gate table on deep links and soft navigations. Nav entries already hide pages a
 * role cannot open, but hiding a link is not access control — a pasted URL bypassed it entirely
 * until this landed (GAIL-785).
 *
 * Client-side by necessity: the `(customers)` server layout does not re-run on soft navigation, so a
 * server guard would only cover full page loads and would let in-app links through. This is a
 * usability backstop, not the security boundary — Galactus enforces scopes on every request.
 */
export default function RouteAccessGuard() {
  const pathname = usePathname();
  const { replace } = useRouter();
  const { isAuthenticated, permissionsDegraded, provisioningPending, user } = useAuthContext();
  const { canAccessAny, permissions } = usePermissions();

  useEffect(() => {
    const decision = decideRouteAccess({
      pathname,
      isAuthenticated,
      agencyId: user?.agencyId,
      permissionsDegraded,
      provisioningPending,
      hasPermissions: permissions !== null,
      canAccessAny,
    });

    if (decision.verdict === "allow" || decision.verdict === "skip") return;

    if (decision.verdict === "redirect") {
      replace(decision.to);
      return;
    }

    if (decision.verdict === "unknown") {
      // Means the table drifted: the coverage test pins every page to a gate, so an unmatched path
      // is either not a page at all or a page whose gate was missed. Deny rather than guess, and
      // warn so the drift is visible instead of only showing up as a confused user.
      console.warn(`[authz] ${decision.reason}; denying`);
    }

    captureAuthzDenied({ source: "route_guard", requestPath: pathname });
    replace(UNAUTHORIZED_PATH);
  }, [
    pathname,
    isAuthenticated,
    permissionsDegraded,
    provisioningPending,
    permissions,
    canAccessAny,
    user,
    replace,
  ]);

  return null;
}
